import { ISHIKAWA_CATEGORY_LABEL_PT_BR, IshikawaCategory } from '@qualicloud/contracts';
import { useCallback, useEffect, useId, useRef, useState, type DragEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  IconAlertTriangle,
  IconChevronDown,
  IconChevronUp,
  IconGrip,
  IconPlus,
  IconTrash,
  cn,
} from '@/design-system';
import type { RootCauseView } from '@/lib/api/sgq.types';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIAGRAMA DE ISHIKAWA — as 6 categorias (6M), em colunas
 * sgq.md §B.6
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * POR QUE COLUNAS E NÃO ESPINHA DE PEIXE
 * A espinha de peixe é um desenho: as causas ficam em diagonais, a ordem
 * visual não corresponde à ordem do DOM e o conjunto só existe como imagem
 * para quem não enxerga. Colunas dão a MESMA informação (6 categorias, causas
 * priorizadas dentro de cada uma) numa estrutura que o leitor de tela percorre
 * como o que ela é: seis listas ordenadas rotuladas. Em tela estreita as
 * colunas empilham; a espinha exigiria rolagem horizontal.
 *
 * O QUE ISTO CORRIGE DO LEGADO
 *  · a categoria era STRING LIVRE de 64 caracteres, sem tabela nem validação:
 *    dois analistas digitando "Metodo" e "Método" produziam duas categorias.
 *    Aqui são os 6M de `IshikawaCategory`, com rótulo de
 *    `ISHIKAWA_CATEGORY_LABEL_PT_BR` — nada é redefinido aqui;
 *  · `sort_order` era gravado literal `0` e NENHUM endpoint o atualizava, então
 *    `ORDER BY tipo, sort_order` degenerava em ordem alfabética de categoria e
 *    arrastar a causa não fazia nada (BUG-REU-09). Aqui reordenar chama
 *    `PATCH /capa/root-causes/:id` e a prioridade vale.
 *
 * ACESSIBILIDADE — WCAG 2.5.7 (movimentos de arrastar)
 * Arrastar NUNCA é o único caminho. Cada causa tem os botões "Subir" e
 * "Descer", que são alcançáveis por Tab e acionáveis por Enter/Espaço; o
 * arrasto com o mouse existe como atalho, sobre o mesmo `onReprioritize`.
 *
 * POR QUE `aria-disabled` E NUNCA `disabled` NESTES BOTÕES
 * Nos extremos da lista — e enquanto a causa está em movimento — o botão fica
 * BLOQUEADO, com o motivo no nome acessível, mas continua focável: nada de
 * `disabled` e nada de sumir. Os dois têm o mesmo efeito colateral, e aqui ele
 * é a regra e não a exceção, porque quem muda de estado é justamente o botão
 * que o usuário acabou de acionar — subir a causa para a posição 1 desabilita
 * o "Subir", o navegador tira o foco dele e o foco cai no `<body>`. Quem
 * navega por teclado seria devolvido ao início de um tabuleiro de seis colunas
 * a cada movimento. Com `aria-disabled` o botão permanece na ordem de
 * tabulação, é anunciado como indisponível e o clique retorna cedo.
 *
 * Pelo mesmo motivo o bloqueio de mutação em curso vale só para a causa
 * acionada: congelar o tabuleiro inteiro tiraria o foco de botões que nada
 * têm a ver com o movimento em andamento.
 *
 * Cada movimento é anunciado num `aria-live="polite"` com a posição resultante
 * ("Causa movida para a posição 2 de 4 em Método"), porque a lista se
 * reordena visualmente e sem o anúncio quem não enxerga não sabe o que houve.
 */

export interface IshikawaGroup {
  readonly category: IshikawaCategory;
  readonly categoryLabel: string;
  readonly causes: readonly RootCauseView[];
}

export interface IshikawaBoardProps {
  readonly groups: readonly IshikawaGroup[];
  /** Bloqueia toda edição — reunião encerrada, por exemplo. */
  readonly readOnly?: boolean;
  /** Nova prioridade da causa dentro da categoria. 1 = mais prioritária. */
  readonly onReprioritize?: (causeId: string, newPriority: number) => void;
  /** Promove a causa a ação 5W2H. É o elo causa → plano de ação. */
  readonly onPromote?: (cause: RootCauseView) => void;
  readonly onAdd?: (category: IshikawaCategory) => void;
  readonly onDelete?: (cause: RootCauseView) => void;
  /** Há mutação em curso. Bloqueia só a causa acionada, não o tabuleiro. */
  readonly busy?: boolean;
  readonly className?: string;
}

/** As seis categorias sempre aparecem, mesmo vazias: a coluna vazia é achado. */
const ALL_CATEGORIES: readonly IshikawaCategory[] = Object.values(IshikawaCategory);

/**
 * Aparência de bloqueado sem `disabled` (ver a nota de acessibilidade acima).
 * `disabledStyles` do design system responde a `:disabled`, que aqui nunca
 * existe — o desligamento visual precisa vir do `aria-disabled`.
 */
const BLOCKED_STYLES =
  'aria-disabled:cursor-not-allowed aria-disabled:opacity-55 aria-disabled:hover:bg-transparent';

export function IshikawaBoard({
  groups,
  readOnly = false,
  onReprioritize,
  onPromote,
  onAdd,
  onDelete,
  busy = false,
  className,
}: IshikawaBoardProps) {
  const baseId = useId();
  const [announcement, setAnnouncement] = useState('');
  const draggedRef = useRef<{ causeId: string; category: IshikawaCategory } | null>(null);

  /**
   * Qual causa está em movimento. O pai manda um `busy` só para o tabuleiro
   * inteiro, mas o que está em curso é sempre UMA causa — guardar qual foi
   * acionada permite bloquear apenas ela e deixar as outras cinco colunas
   * operáveis.
   */
  const [pendingCauseId, setPendingCauseId] = useState<string | null>(null);
  useEffect(() => {
    if (!busy) setPendingCauseId(null);
  }, [busy]);

  const byCategory = new Map(groups.map((group) => [group.category, group]));

  const move = useCallback(
    (
      category: IshikawaCategory,
      causes: readonly RootCauseView[],
      index: number,
      target: number,
    ) => {
      const cause = causes[index];
      if (!cause || target < 0 || target >= causes.length || !onReprioritize) return;
      setPendingCauseId(cause.id);
      // A prioridade é 1-based e é a POSIÇÃO desejada. O servidor reordena os
      // vizinhos; o cliente não recalcula a lista inteira para não divergir.
      onReprioritize(cause.id, target + 1);
      setAnnouncement(
        `Causa "${cause.description}" movida para a posição ${target + 1} de ${causes.length} em ${
          ISHIKAWA_CATEGORY_LABEL_PT_BR[category]
        }.`,
      );
    },
    [onReprioritize],
  );

  const totalCauses = groups.reduce((sum, group) => sum + group.causes.length, 0);

  return (
    <section aria-labelledby={`${baseId}-heading`} className={cn('min-w-0', className)}>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h3 id={`${baseId}-heading`} className="text-base font-semibold text-content">
            Análise de causa — diagrama de Ishikawa
          </h3>
          <p className="mt-1 max-w-3xl text-sm text-content-muted">
            As seis categorias clássicas (6M). Dentro de cada uma, as causas ficam em ordem de
            prioridade: 1 é a mais prioritária. Use os botões Subir e Descer — ou arraste com o
            mouse — para reordenar. Promover uma causa cria a ação 5W2H que a ataca.
          </p>
        </div>
        <p className="shrink-0 text-sm text-content-muted qc-tabular">
          {totalCauses === 1 ? '1 causa registrada' : `${totalCauses} causas registradas`}
        </p>
      </div>

      {totalCauses === 0 && readOnly ? (
        <EmptyState
          icon={<IconAlertTriangle size={22} />}
          title="Nenhuma causa registrada"
          description="A análise de causa desta não-conformidade ainda não foi feita. Sem causa-raiz determinada, o plano de ação trata sintoma — ISO 13485 §8.5.2 exige determinar a causa antes de agir."
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {ALL_CATEGORIES.map((category) => {
            const group = byCategory.get(category);
            const causes = group?.causes ?? [];
            const listId = `${baseId}-${category}`;

            return (
              <Card key={category} padding="none" className="flex min-w-0 flex-col">
                <div className="flex items-center justify-between gap-2 border-b border-border-subtle bg-surface-sunken px-3 py-2">
                  <h4
                    id={`${listId}-heading`}
                    className="min-w-0 truncate text-sm font-semibold text-content"
                  >
                    {ISHIKAWA_CATEGORY_LABEL_PT_BR[category]}
                  </h4>
                  <Badge tone="neutral" size="sm">
                    {causes.length}
                  </Badge>
                </div>

                {causes.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-content-subtle">
                    Nenhuma causa nesta categoria.
                  </p>
                ) : (
                  <ol
                    aria-labelledby={`${listId}-heading`}
                    className="flex min-w-0 flex-col gap-1.5 p-2"
                  >
                    {causes.map((cause, index) => {
                      const moving = busy && pendingCauseId === cause.id;
                      const atTop = index === 0;
                      const atBottom = index === causes.length - 1;
                      const hasLinkedAction = cause.actionCount > 0;

                      return (
                        <li
                          key={cause.id}
                          draggable={!readOnly && Boolean(onReprioritize)}
                          onDragStart={() => {
                            draggedRef.current = { causeId: cause.id, category };
                          }}
                          onDragOver={(event: DragEvent<HTMLLIElement>) => {
                            if (draggedRef.current?.category === category) event.preventDefault();
                          }}
                          onDrop={(event: DragEvent<HTMLLIElement>) => {
                            event.preventDefault();
                            const dragged = draggedRef.current;
                            draggedRef.current = null;
                            if (!dragged || dragged.category !== category) return;
                            const from = causes.findIndex((item) => item.id === dragged.causeId);
                            if (from < 0 || from === index) return;
                            move(category, causes, from, index);
                          }}
                          className={cn(
                            'rounded-md border border-border-subtle bg-surface px-2.5 py-2',
                            cause.isRootCause && 'border-l-4 border-l-brand',
                          )}
                        >
                          <div className="flex items-start gap-2">
                            {/* Alça visual do arrasto. `aria-hidden` porque o
                                caminho acessível são os botões Subir/Descer
                                abaixo — anunciar uma alça inoperante por
                                teclado seria pior do que não anunciá-la. */}
                            {!readOnly && onReprioritize ? (
                              <span aria-hidden="true" className="mt-0.5 text-content-subtle">
                                <IconGrip size={14} />
                              </span>
                            ) : null}

                            <span
                              aria-hidden="true"
                              className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-2xs font-semibold text-content-muted qc-tabular"
                            >
                              {index + 1}
                            </span>

                            <div className="min-w-0 flex-1">
                              <p className="break-words text-sm text-content">
                                <span className="qc-sr-only">
                                  Prioridade {index + 1} de {causes.length}.{' '}
                                </span>
                                {cause.description}
                              </p>
                              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                {cause.isRootCause ? (
                                  <Badge tone="brand" size="sm">
                                    Causa-raiz confirmada
                                  </Badge>
                                ) : null}
                                {hasLinkedAction ? (
                                  <Badge tone="success" size="sm">
                                    {cause.actionCount === 1
                                      ? '1 ação vinculada'
                                      : `${cause.actionCount} ações vinculadas`}
                                  </Badge>
                                ) : (
                                  <Badge tone="outline" size="sm">
                                    Sem ação
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>

                          {!readOnly ? (
                            <div
                              role="group"
                              aria-label={`Ações para a causa ${cause.description}`}
                              className="mt-1.5 flex flex-wrap items-center gap-1"
                            >
                              {onReprioritize ? (
                                <>
                                  {/* O nome acessível explica o bloqueio
                                      PERMANENTE (o extremo da lista). O
                                      bloqueio momentâneo do movimento em curso
                                      não entra no nome de propósito: trocar o
                                      nome do botão no meio da interação faz o
                                      leitor de tela reanunciá-lo; o estado
                                      `aria-disabled` já diz "indisponível". */}
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    aria-disabled={moving || atTop || undefined}
                                    className={BLOCKED_STYLES}
                                    iconLabel={
                                      atTop
                                        ? `"${cause.description}" já é a causa mais prioritária de ${ISHIKAWA_CATEGORY_LABEL_PT_BR[category]}`
                                        : `Subir "${cause.description}" para a posição ${index} de ${causes.length}`
                                    }
                                    onClick={() => {
                                      if (moving || atTop) return;
                                      move(category, causes, index, index - 1);
                                    }}
                                  >
                                    <IconChevronUp size={15} />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    aria-disabled={moving || atBottom || undefined}
                                    className={BLOCKED_STYLES}
                                    iconLabel={
                                      atBottom
                                        ? `"${cause.description}" já é a causa menos prioritária de ${ISHIKAWA_CATEGORY_LABEL_PT_BR[category]}`
                                        : `Descer "${cause.description}" para a posição ${index + 2} de ${causes.length}`
                                    }
                                    onClick={() => {
                                      if (moving || atBottom) return;
                                      move(category, causes, index, index + 1);
                                    }}
                                  >
                                    <IconChevronDown size={15} />
                                  </Button>
                                </>
                              ) : null}

                              {onPromote ? (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  aria-disabled={moving || undefined}
                                  className={BLOCKED_STYLES}
                                  onClick={() => {
                                    if (moving) return;
                                    onPromote(cause);
                                  }}
                                >
                                  Criar ação corretiva
                                  <span className="qc-sr-only">
                                    {' '}
                                    5W2H para a causa {cause.description}
                                  </span>
                                </Button>
                              ) : null}

                              {onDelete ? (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  aria-disabled={moving || hasLinkedAction || undefined}
                                  className={BLOCKED_STYLES}
                                  iconLabel={
                                    hasLinkedAction
                                      ? `Não é possível remover "${cause.description}": há ação corretiva vinculada a esta causa`
                                      : `Remover a causa "${cause.description}"`
                                  }
                                  onClick={() => {
                                    if (moving || hasLinkedAction) return;
                                    setPendingCauseId(cause.id);
                                    onDelete(cause);
                                  }}
                                >
                                  <IconTrash size={15} />
                                </Button>
                              ) : null}
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ol>
                )}

                {!readOnly && onAdd ? (
                  <div className="border-t border-border-subtle p-2">
                    {/* Também sem `disabled`: ao fechar, o diálogo devolve o
                        foco a este botão, e um botão desabilitado não recebe
                        foco — o usuário voltaria para o `<body>`. */}
                    <Button
                      variant="ghost"
                      size="sm"
                      leadingIcon={<IconPlus size={14} />}
                      aria-disabled={busy || undefined}
                      onClick={() => {
                        if (busy) return;
                        onAdd(category);
                      }}
                      className={cn('w-full justify-start', BLOCKED_STYLES)}
                    >
                      Adicionar causa
                      <span className="qc-sr-only">
                        {' '}
                        em {ISHIKAWA_CATEGORY_LABEL_PT_BR[category]}
                      </span>
                    </Button>
                  </div>
                ) : null}
              </Card>
            );
          })}
        </div>
      )}

      {/* Anúncio do movimento. Fora das listas para não ser reordenado junto. */}
      <span aria-live="polite" className="qc-sr-only">
        {announcement}
      </span>

      <p className="mt-3 text-xs text-content-subtle">
        Reordenar por teclado: use Tab até os botões Subir e Descer da causa e acione com Enter ou
        Espaço. O arrasto com o mouse é apenas um atalho para o mesmo comando.
      </p>
    </section>
  );
}
